import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginLinkDto, UserProfile } from '@motoschool/shared';
import { formatDuration } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import {
  Avatar,
  Badge,
  Button,
  Card,
  cn,
  CopyableLink,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageLoader,
  Sheet,
  SuccessNotice,
} from '../components/ui';

interface StudentRow extends UserProfile {
  balanceMinutes: number | null;
  creditedMinutes: number | null;
  spentMinutes: number | null;
}

/**
 * Сколько часов ученик уже отзанимался из оплаченных. Одно число баланса
 * этого не показывает: «9 ч» одинаково выглядит и у новичка, который только
 * оплатил, и у того, кто отзанимался почти всё.
 */
/**
 * Часы в компактном виде: «8,5 ч» вместо «8 ч 30 мин». В соотношении рядом
 * стоят два значения, и полная форма занимает две строки в узкой колонке.
 */
function shortHours(minutes: number): string {
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return `${String(rounded).replace('.', ',')} ч`;
}

function HoursProgress({ spent, credited }: { spent: number; credited: number }): React.JSX.Element {
  // Долг уводит долю за сотню — полосу это ломает, поэтому она упирается в край.
  const ratio = credited > 0 ? Math.min(spent / credited, 1) : 0;
  const isOverspent = spent > credited;

  return (
    <div className="w-24 shrink-0">
      <p className="whitespace-nowrap text-right text-sm font-medium tabular-nums">
        {shortHours(spent)}
        <span className="text-muted"> / {shortHours(credited)}</span>
      </p>
      <div
        className="mt-1.5 h-1 overflow-hidden rounded-full bg-line"
        role="presentation"
        title={`Отзанимался ${formatDuration(spent)} из оплаченных ${formatDuration(credited)}`}
      >
        <div
          className={cn('h-full rounded-full transition-all', isOverspent ? 'bg-danger' : 'bg-ink')}
          style={{ width: `${Math.max(ratio * 100, credited > 0 && spent > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
}

interface StudentsResponse {
  items: StudentRow[];
  total: number;
}

export function StudentsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', hours: '' });
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{
    login: string | null;
    password?: string;
    link?: LoginLinkDto;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', 'list', search],
    queryFn: () =>
      api.get<StudentsResponse>('/users', {
        role: 'STUDENT',
        search: search || undefined,
        pageSize: 100,
      }),
  });

  const createStudent = useMutation({
    mutationFn: async () => {
      const hours = Number(form.hours.replace(',', '.'));

      const result = await api.post<{ user: UserProfile; password?: string }>('/users', {
        name: form.name,
        role: 'STUDENT',
        // Телефон и есть логин ученика: почту у него не спрашивают,
        // а войти по номеру он может с любого телефона.
        phone: form.phone,
        initialHours: form.hours.trim() === '' ? undefined : hours,
      });

      // Ссылка выпускается сразу: чаще всего ученика заводят, чтобы тут же
      // отправить ему вход, и отдельный поход на карточку только мешает.
      const { link } = await api.post<{ link: LoginLinkDto }>(
        `/users/${result.user.id}/login-link`,
        {},
      );

      return { ...result, link };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsAdding(false);
      setForm({ name: '', phone: '', hours: '' });
      setCreated({ login: result.user.phone, password: result.password, link: result.link });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiRequestError ? caught.message : 'Не удалось добавить ученика');
    },
  });

  const students = data?.items ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Ученики</h1>
          <p className="text-sm text-muted">Баланс часов и история занятий</p>
        </div>
        <Button
          onClick={() => {
            setError(null);
            setIsAdding(true);
          }}
        >
          <span aria-hidden>＋</span> Ученик
        </Button>
      </div>

      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Поиск по имени, телефону или Telegram"
        type="search"
      />

      {created && (
        <SuccessNotice>
          <div className="space-y-2">
            {created.password && (
              <p>
                Вход: логин <b className="tabular-nums">{created.login ?? '—'}</b>, пароль{' '}
                <b>{created.password}</b>. Пароль показывается один раз.
              </p>
            )}

            {created.link && (
              <div>
                <p className="mb-1">Ссылка для входа в кабинет — отправьте её ученику:</p>
                <CopyableLink url={created.link.url} />
              </div>
            )}

            <button type="button" className="underline" onClick={() => setCreated(null)}>
              Скрыть
            </button>
          </div>
        </SuccessNotice>
      )}

      {isLoading ? (
        <PageLoader />
      ) : students.length === 0 ? (
        <EmptyState
          title="Учеников пока нет"
          description="Добавьте ученика вручную или дождитесь заявки по публичной ссылке."
        />
      ) : (
        <div className="space-y-2">
          {students.map((student) => {
            const balance = student.balanceMinutes ?? 0;
            const spent = student.spentMinutes ?? 0;
            const credited = student.creditedMinutes ?? 0;

            return (
              <Link key={student.id} to={`/students/${student.id}`} className="block">
                <Card className="transition-colors hover:border-brand-200">
                  <div className="flex items-center gap-3">
                    <Avatar name={student.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{student.name}</p>
                      <p className="truncate text-xs text-muted">
                        {student.phone ?? student.email ?? 'Контакты не указаны'}
                      </p>
                    </div>

                    <HoursProgress spent={spent} credited={credited} />

                    <div className="w-20 text-right">
                      {/* Долг по часам — главное, что инструктор ищет в списке */}
                      <Badge tone={balance < 0 ? 'danger' : balance === 0 ? 'neutral' : 'success'}>
                        {formatDuration(balance)}
                      </Badge>
                      {!student.isActive && (
                        <p className="mt-1 text-xs text-muted">отключён</p>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      <Sheet
        isOpen={isAdding}
        title="Новый ученик"
        onClose={() => setIsAdding(false)}
        footer={
          <div className="flex gap-2 pb-1">
            <Button variant="secondary" fullWidth onClick={() => setIsAdding(false)}>
              Отмена
            </Button>
            <Button
              fullWidth
              isLoading={createStudent.isPending}
              onClick={() => createStudent.mutate()}
            >
              Добавить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}

          <Field label="Имя и фамилия" required>
            <Input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>

          <Field label="Телефон" hint="Он же логин для входа на сайт" required>
            <Input
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 701 000-00-00"
            />
          </Field>

          <Field label="Оплачено часов" hint="Можно оставить пустым и начислить позже">
            <Input
              value={form.hours}
              onChange={(event) => setForm({ ...form, hours: event.target.value })}
              type="number"
              inputMode="decimal"
              min={0}
              max={500}
              step={0.5}
              placeholder="10"
            />
          </Field>

          <p className="text-xs text-muted">
            Пароль и ссылка для входа появятся после сохранения.
          </p>
        </div>
      </Sheet>
    </div>
  );
}
